export type ProfileAvatarCropTransform = {
  /** Multiplier on top of the base cover scale (1 = fill crop frame). */
  zoom: number;
  /** Pan from center in preview pixels. */
  offsetX: number;
  offsetY: number;
};

export const PROFILE_AVATAR_CROP_PREVIEW_SIZE = 280;
export const PROFILE_AVATAR_CROP_OUTPUT_SIZE = 512;
export const PROFILE_AVATAR_CROP_MIN_ZOOM = 1;
export const PROFILE_AVATAR_CROP_MAX_ZOOM = 3;

export function createDefaultCropTransform(): ProfileAvatarCropTransform {
  return { zoom: 1, offsetX: 0, offsetY: 0 };
}

export function baseCoverScale(
  imageWidth: number,
  imageHeight: number,
  frameSize: number,
): number {
  if (imageWidth < 1 || imageHeight < 1 || frameSize < 1) {
    return 1;
  }
  return Math.max(frameSize / imageWidth, frameSize / imageHeight);
}

export function clampCropTransform(
  transform: ProfileAvatarCropTransform,
  imageWidth: number,
  imageHeight: number,
  frameSize: number = PROFILE_AVATAR_CROP_PREVIEW_SIZE,
): ProfileAvatarCropTransform {
  const zoom = Math.min(
    PROFILE_AVATAR_CROP_MAX_ZOOM,
    Math.max(PROFILE_AVATAR_CROP_MIN_ZOOM, transform.zoom),
  );
  const scale = baseCoverScale(imageWidth, imageHeight, frameSize) * zoom;
  const drawnW = imageWidth * scale;
  const drawnH = imageHeight * scale;
  const maxOffsetX = Math.max(0, (drawnW - frameSize) / 2);
  const maxOffsetY = Math.max(0, (drawnH - frameSize) / 2);
  return {
    zoom,
    offsetX: Math.min(maxOffsetX, Math.max(-maxOffsetX, transform.offsetX)),
    offsetY: Math.min(maxOffsetY, Math.max(-maxOffsetY, transform.offsetY)),
  };
}

export function cropTransformToPreviewStyle(
  transform: ProfileAvatarCropTransform,
  imageWidth: number,
  imageHeight: number,
  frameSize: number = PROFILE_AVATAR_CROP_PREVIEW_SIZE,
): { width: number; height: number; x: number; y: number } {
  const scale = baseCoverScale(imageWidth, imageHeight, frameSize) * transform.zoom;
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  const x = frameSize / 2 + transform.offsetX - width / 2;
  const y = frameSize / 2 + transform.offsetY - height / 2;
  return { width, height, x, y };
}

export function renderCroppedAvatarDataUrl(
  image: CanvasImageSource & { width: number; height: number },
  transform: ProfileAvatarCropTransform,
  frameSize: number = PROFILE_AVATAR_CROP_PREVIEW_SIZE,
  outputSize: number = PROFILE_AVATAR_CROP_OUTPUT_SIZE,
): string | null {
  const clamped = clampCropTransform(transform, image.width, image.height, frameSize);
  const placement = cropTransformToPreviewStyle(
    clamped,
    image.width,
    image.height,
    frameSize,
  );
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const outScale = outputSize / frameSize;
  ctx.beginPath();
  ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(
    image,
    placement.x * outScale,
    placement.y * outScale,
    placement.width * outScale,
    placement.height * outScale,
  );
  return canvas.toDataURL('image/jpeg', 0.88);
}

export function revokeProfileAvatarObjectUrl(image: HTMLImageElement | null): void {
  if (image?.src.startsWith('blob:')) {
    URL.revokeObjectURL(image.src);
  }
}

export async function loadImageElementFromFile(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Could not load image'));
    image.src = url;
  });
  return image;
}
